<script setup>
import ActionButton from '@js/components/buttons/ActionButton.vue';
import { goBack } from '@js/utils/route';
import {
    TooltipContent,
    TooltipPortal,
    TooltipProvider,
    TooltipRoot,
    TooltipTrigger,
} from 'reka-ui';
import { watch } from 'vue';
import { inject } from 'vue';

const {
    errors,
    isSubmitting,
    withoutBackButton = false,
    withoutSaveButton = false,
    submitText = 'Enregistrer',
    submittingText = 'Enregistrement en cours...',
    disabled = false,
} = defineProps({
    errors: {
        type: Object,
        required: true,
    },
    isSubmitting: {
        type: Boolean,
        required: true,
    },
    disabled: {
        type: Boolean,
        required: false,
    },
    withoutBackButton: {
        type: Boolean,
        required: false,
    },
    withoutSaveButton: {
        type: Boolean,
        required: false,
    },
    submitText: {
        type: String,
        required: false,
    },
    submittingText: {
        type: String,
        required: false,
    },
});

watch(
    () => errors,
    (newErrors) => {
        console.error(newErrors);
    },
);

const isInDialog = inject('isInDialog', false);
</script>

<template>
    <div
        class="sticky -bottom-2 z-10 flex flex-col items-start gap-2 border-gray-200 bg-white p-6"
        :class="{
            'mt-4 rounded border': !isInDialog,
            'bottom-0! mt-auto border-t': isInDialog,
        }"
    >
        <div v-if="Object.keys(errors).length > 0" class="relative">
            <TooltipProvider>
                <TooltipRoot>
                    <TooltipTrigger as-child>
                        <span
                            class="cursor-help text-sm text-red-500 underline"
                        >
                            Le formulaire contient des erreurs.
                        </span>
                    </TooltipTrigger>

                    <TooltipPortal>
                        <TooltipContent
                            side="top"
                            :side-offset="5"
                            class="z-50 rounded-sm border border-gray-300 bg-white px-5 py-2 text-sm shadow-sm"
                        >
                            <ul class="list-disc space-y-1 pl-4 text-red-500">
                                <li
                                    v-for="(message, field) in errors"
                                    :key="field"
                                >
                                    {{ message }} <small>({{ field }})</small>
                                </li>
                            </ul>
                        </TooltipContent>
                    </TooltipPortal>
                </TooltipRoot>
            </TooltipProvider>
        </div>

        <div class="flex flex-wrap gap-2">
            <ActionButton
                v-if="!withoutBackButton"
                type="transparent"
                :callback="() => goBack()"
            >
                Retour
            </ActionButton>

            <slot name="actions" />

            <ActionButton
                v-if="!withoutSaveButton"
                submit
                :disabled="
                    Object.keys(errors).length > 0 || isSubmitting || disabled
                "
                :loading="isSubmitting"
            >
                <template v-if="!isSubmitting"> {{ submitText }} </template>

                <template v-else> {{ submittingText }} </template>
            </ActionButton>
        </div>
    </div>
</template>
